import { Head, Link, router } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import { formatDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

interface Customer {
  id: number
  display_name: string
}

interface Document {
  id: number
  name: string | null
  document_date: string | null
  document_type: string | null
  notes: string | null
  filename: string | null
  filetype: string | null
  download_url: string | null
  created_at: string
  created_by: string | null
}

interface Props {
  customer: Customer
  document: Document
  can_edit: boolean
  can_delete: boolean
}

function formatDateTime(dateStr: string): string {
  return formatDate(dateStr)
}

export default function Show({
  customer,
  document: doc,
  can_edit,
  can_delete,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Customers", href: "/customers" },
    { title: customer.display_name, href: `/customers/${customer.id}` },
    { title: "Documents", href: `/customers/${customer.id}/documents` },
    {
      title: doc.name ?? "Document",
      href: `/customers/${customer.id}/documents/${doc.id}`,
    },
  ]

  const handleDelete = () => {
    if (confirm("Are you sure you want to delete this document?")) {
      router.delete(`/customers/${customer.id}/documents/${doc.id}`)
    }
  }

  const labelClass = "text-sm font-medium text-hpi-med-gray"
  const valueClass = "mt-1 text-hpi-dark-gray"

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`${doc.name ?? "Document"} - ${customer.display_name}`} />

      <div className="p-6">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">
            {doc.name ?? "Document Details"}
          </h1>
          <div className="flex space-x-3">
            {can_edit && (
              <Link
                href={`/customers/${customer.id}/documents/${doc.id}/edit`}
                className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
              >
                Edit
              </Link>
            )}
            {can_delete && (
              <button
                onClick={handleDelete}
                className="bg-hpi-red rounded px-4 py-2 text-white hover:bg-red-700"
              >
                Delete
              </button>
            )}
          </div>
        </div>

        <div className="max-w-2xl space-y-4">
          <div>
            <p className={labelClass}>Type</p>
            <p className={valueClass}>{doc.document_type ?? "—"}</p>
          </div>

          <div>
            <p className={labelClass}>Date</p>
            <p className={valueClass}>{formatDate(doc.document_date) || "—"}</p>
          </div>

          <div>
            <p className={labelClass}>Description</p>
            <p className={valueClass}>{doc.name ?? "—"}</p>
          </div>

          <div>
            <p className={labelClass}>Notes</p>
            <p className={valueClass + " whitespace-pre-wrap"}>
              {doc.notes ?? "—"}
            </p>
          </div>

          {doc.download_url && (
            <div>
              <p className={labelClass}>Document</p>
              <p className={valueClass}>
                <a
                  href={doc.download_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-hpi-active-blue hover:underline"
                >
                  View Document ({doc.filename})
                </a>
              </p>
            </div>
          )}

          <div className="border-hpi-light-gray border-t pt-4">
            <p className="text-hpi-med-gray text-xs">
              Added on {formatDateTime(doc.created_at)}
              {doc.created_by && <> by {doc.created_by}</>}
            </p>
          </div>
        </div>

        <div className="mt-6">
          <Link
            href={`/customers/${customer.id}/documents`}
            className="text-hpi-active-blue hover:underline"
          >
            Back to Documents
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
